import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { programData } from '../data/course';
import {
  HiOutlinePlay,
  HiOutlineBookOpen,
  HiOutlineClipboardDocumentCheck,
} from 'react-icons/hi2';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

function IncludedCard({ item, index }) {
  return (
    <motion.div
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-40px" }}
      variants={fadeUp}
      className="group relative rounded-xl border border-gray-950/[0.06] bg-white p-6 hover:bg-gray-950/[0.03] transition-colors duration-150 cursor-default"
    >
      <p className="text-[14px] font-semibold text-gray-900 leading-snug mb-1.5">
        {item.label}
      </p>
      <p className="text-[13px] text-gray-500 leading-relaxed">
        {item.detail}
      </p>
    </motion.div>
  );
}

function ContentRow({ to, icon: Icon, label, meta, description }) {
  return (
    <Link
      to={to}
      className="group/item flex items-start gap-3.5 -mx-3 px-3 py-3 rounded-xl hover:bg-gray-950/[0.03] transition-colors"
    >
      <div className="mt-0.5 flex-shrink-0">
        <span className={`flex h-5 w-5 items-center justify-center text-gray-400 group-hover/item:text-gray-600 transition-colors`}>
          <Icon className="w-[18px] h-[18px]" />
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3">
          <span className="text-[15px] font-semibold text-gray-900">
            {label}
          </span>
          {meta && (
            <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
              {meta}
            </span>
          )}
        </div>
        {description && (
          <p className="text-[14px] text-gray-500 mt-0.5 line-clamp-2">
            {description}
          </p>
        )}
      </div>
    </Link>
  );
}

function WeekSection({ week, index }) {
  return (
    <motion.section
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      variants={fadeUp}
      className="border-t border-gray-950/[0.08] pt-10 sm:pt-12"
    >
      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-6 sm:gap-10">
        {/* Left — week label */}
        <div>
          <div className="w-8 border-t-2 border-gray-900 mb-4" />
          <p className="text-sm text-gray-500">Week {week.number}</p>
        </div>

        {/* Right — content */}
        <div>
          <h3 className="text-2xl font-semibold text-gray-900 tracking-tight mb-2">
            {week.title}
          </h3>
          <p className="text-[15px] text-gray-500 leading-relaxed mb-3 max-w-2xl italic">
            {week.tagline}
          </p>
          <p className="text-[15px] text-gray-500 leading-relaxed mb-8 max-w-2xl">
            {week.description}
          </p>

          <div className="space-y-0">
            {/* Live Session */}
            <ContentRow
              to={`/content/${week.liveSession.id}`}
              icon={HiOutlinePlay}
              label={week.liveSession.title}
              meta={week.liveSession.duration}
              description={
                week.liveSession.description.length > 120
                  ? week.liveSession.description.slice(0, 120) + '...'
                  : week.liveSession.description
              }
            />

            {/* Articles */}
            {week.articles.map((article) => (
              <ContentRow
                key={article.id}
                to={`/content/${article.id}`}
                icon={HiOutlineBookOpen}
                label={article.shortTitle}
                meta={article.readingTime}
                description={
                  article.description.length > 120
                    ? article.description.slice(0, 120) + '...'
                    : article.description
                }
              />
            ))}

            {/* Worksheets */}
            {week.worksheets.map((ws) => (
              <ContentRow
                key={ws.id}
                to={`/content/${ws.id}`}
                icon={HiOutlineClipboardDocumentCheck}
                label={ws.shortTitle}
                meta={ws.completionTime}
                description={
                  ws.description.length > 120
                    ? ws.description.slice(0, 120) + '...'
                    : ws.description
                }
              />
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}

export default function CourseOverview() {
  return (
    <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 pb-24">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative rounded-2xl bg-[#0a0a0b] overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/95 to-[#0a0a0b]/70" />

        <div className="relative flex items-center">
          {/* Left — text content */}
          <div className="flex-1 px-8 sm:px-10 py-16 sm:py-20">
            <div className="max-w-md">
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="text-3xl sm:text-4xl font-semibold text-white tracking-tight leading-tight"
              >
                {programData.title}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="text-[15px] text-white/45 mt-2"
              >
                with Dr. Shilpa Saxena, MD
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="text-base/7 text-white/50 mt-5 max-w-lg"
              >
                {programData.description}
              </motion.p>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/40 mt-6"
              >
                <span>{programData.stats.weeks} weeks</span>
                <span className="text-white/15">&middot;</span>
                <span>{programData.stats.articles} articles</span>
                <span className="text-white/15">&middot;</span>
                <span>{programData.stats.worksheets} worksheets</span>
                <span className="text-white/15">&middot;</span>
                <span>6 hr total</span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="mt-8"
              >
                <Link
                  to="/content/week-1-live"
                  className="inline-flex items-center gap-x-2 bg-white text-gray-900 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  <HiOutlinePlay className="w-4 h-4" />
                  Start the program
                </Link>
              </motion.div>
            </div>
          </div>

          {/* Right — Dr. Shilpa photo, edge-to-edge on top/right/bottom */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="hidden md:block flex-shrink-0 w-72 lg:w-96 self-stretch relative"
          >
            <img
              src="https://forumhealth.com/wp-content/uploads/2019/05/Shilpa-Saxena-1-scaled-e1699545138555-768x768.webp"
              alt="Dr. Shilpa Saxena"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            {/* Soft wide gradient blending into the dark bg */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/30 via-25% to-transparent" />
          </motion.div>
        </div>
      </motion.div>

      {/* What's Included */}
      <section className="pt-16 sm:pt-20 pb-4">
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-sm font-semibold text-gray-900 mb-6"
        >
          What's included
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {programData.whatsIncluded.map((item, i) => (
            <IncludedCard key={i} item={item} index={i} />
          ))}
        </div>
      </section>

      {/* Week sections */}
      {programData.weeks.map((week, i) => (
        <WeekSection key={week.number} week={week} index={i} />
      ))}

      {/* About */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        custom={0}
        className="border-t border-gray-950/[0.08] pt-12 mt-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-6 sm:gap-10">
          <div>
            <div className="w-8 border-t-2 border-gray-900 mb-4" />
            <p className="text-sm text-gray-500">Your guide</p>
          </div>
          <Link
            to="/about"
            className="group flex items-start gap-5 -mx-3 px-3 py-4 rounded-xl hover:bg-gray-950/[0.03] transition-colors"
          >
            <div className="w-14 h-14 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden">
              <img
                src="https://forumhealth.com/wp-content/uploads/2019/05/Shilpa-Saxena-1-scaled-e1699545138555-768x768.webp"
                alt="Dr. Shilpa Saxena"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="text-[15px] font-semibold text-gray-900">
                {programData.about.doctor.name}
              </p>
              <p className="text-[14px] text-gray-500 mt-0.5">
                {programData.about.doctor.title}
              </p>
              <p className="text-[14px] text-gray-500 mt-3 leading-relaxed max-w-xl">
                {programData.about.doctor.description}
              </p>
              <span className="inline-flex items-center gap-1.5 text-[13px] text-gray-400 group-hover:text-gray-600 mt-3 transition-colors">
                View full profile
                <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                  <path fillRule="evenodd" d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
          </Link>
        </div>
      </motion.section>
    </div>
  );
}
